import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Breadcrumb, Input, Table, Space, Popconfirm, Col, Row, Tooltip } from "antd";
import { getRequest, postRequest } from "../../axios";
import SearchIcon from "../../image/SearchIcon.svg";
import "antd/dist/antd.min.css";
import "../../styles/Main.scss";
import { ReactComponent as ArchiveRestore } from "../../image/ArchiveRestore.svg";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";

const { Search } = Input;

const ArchivedMaterialListCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archivedMaterialList, setArchivedMaterialList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);

  const [state, setState] = useState({
    page: 1,
    ordering: "-id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  useEffect(() => {
    const getMaterialArchiveList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("material-master-archive/", state);
        if (apiResponse.data.results.success) {
          setArchivedMaterialList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_archived_material"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getMaterialArchiveList();
  }, [state, t]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "-id";

    if (sortField === "code" && sortOrder === "ascend") {
      sortingEnumKey = "code";
    }
    if (sortField === "code" && sortOrder === "descend") {
      sortingEnumKey = "-code";
    }
    if (sortField === "category_tree_name" && sortOrder === "ascend") {
      sortingEnumKey = "category_tree__name";
    }
    if (sortField === "category_tree_name" && sortOrder === "descend") {
      sortingEnumKey = "-category_tree__name";
    }

    if (sortField === "material_type_name" && sortOrder === "ascend") {
      sortingEnumKey = "material_type__type_name";
    }
    if (sortField === "material_type_name" && sortOrder === "descend") {
      sortingEnumKey = "-material_type__type_name";
    }
    if (sortField === "short_description" && sortOrder === "ascend") {
      sortingEnumKey = "short_description";
    }
    if (sortField === "short_description" && sortOrder === "descend") {
      sortingEnumKey = "-short_description";
    }
    if (sortField === "long_description" && sortOrder === "ascend") {
      sortingEnumKey = "long_description";
    }
    if (sortField === "long_description" && sortOrder === "descend") {
      sortingEnumKey = "-long_description";
    }

    if (sortField === "unit_of_measurement" && sortOrder === "ascend") {
      sortingEnumKey = "unit_of_measurement";
    }
    if (sortField === "unit_of_measurement" && sortOrder === "descend") {
      sortingEnumKey = "-unit_of_measurement";
    }

    if (sortField === "image" && sortOrder === "ascend") {
      sortingEnumKey = "image";
    }
    if (sortField === "image" && sortOrder === "descend") {
      sortingEnumKey = "-image";
    }

    return sortingEnumKey;
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };

  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("material-master-archive56/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("material_restore_successfully"));
        setSelectedRow([]);
        navigate("/company-material");
      } else {
        ErrorNotificationMsg(t("error_in_restore_material"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };
  const openImageInNewTab = (imageSrc) => {
    window.open(imageSrc, "_blank");
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      sorter: true,
    },
    {
      title: "Category",
      dataIndex: "category_tree_name",
      key: "category_tree_name",
      sorter: true,
    },
    {
      title: "Material Type",
      dataIndex: "material_type_name",
      key: "material_type_name",
      sorter: true,
    },

    {
      title: "Short Description",
      dataIndex: "short_description",
      key: "short_description",
      sorter: true,
    },
    {
      title: "Long Description",
      dataIndex: "long_description",
      key: "long_description",
      sorter: true,
    },
    {
      title: "Unit Of Measurement",
      dataIndex: "unit_of_measurement",
      key: "unit_of_measurement",
      sorter: true,
    },
    {
      title: "Images",
      dataIndex: "image",
      key: "image",
      sorter: true,
      render: (image) =>
        image ? (
          <a href={image} target="_blank" rel="noopener noreferrer">
            <img
              src={image}
              alt="Category thumbnail"
              style={{ width: "50px", height: "50px" }}
              onClick={() => openImageInNewTab(image)}
            />
          </a>
        ) : (
          ""
        ),
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: "100px",
      render: (text, record) => (
        <Space>
          <Popconfirm
            className="action"
            title={t("pop_restore_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <ArchiveRestore className="ArchiverestorImg" />{" "}
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
          <Tooltip
              color={colors}
              placement="right"
              title={
                <span style={{ color: "#fff" }}>
                  View details of the archived material, including description, category, and supplier.
                </span>
              }
            >
            <h3>{t("archived_material")}</h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/company-material">{t("material")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("archiveList")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5">
        <div className="PR-body">
          <Row className="tblheader">
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              className="tbl-heading"
            >
              {/* <h1></h1> */}
            </Col>
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-action">
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Col> */}
          </Row>

          <Table
            scroll={{
              x: 2000,
            }}
            rowSelection={rowSelection}
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={archivedMaterialList}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (totalCount) => `Total ${totalCount} items`,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
           <div className="FormFooter Container">
            <ButtonField
              className="CancelGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("previous")}
              onClick={() =>
                navigate(
                  `/company-material`
                )
              }
            //onClick={() => setCurrentIndex(0)}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default ArchivedMaterialListCompany;
